import { Box, Flex } from '@modusign/design-system';
import { useGetAllDocuments } from '@modusign/openapi-client-document';
import {
  useGetUserInfo,
  useGetWorkspaceSurvey,
  useSaveSurvey,
  useSaveWorkspaceSurvey,
} from '@modusign/openapi-client-user';
import parsePhoneNumberFromString, {
  type CountryCode,
  isSupportedCountry,
} from 'libphonenumber-js';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { ModusignLogoFull } from '@app/components/Logos/ModusignLogo';
import CompanyNameStep from '@app/containers/Modal/WelcomeSurveyModal/CompanyNameStep';
import CompanyScaleStep from '@app/containers/Modal/WelcomeSurveyModal/CompanyScaleStep';
import DepartmemtAndPhoneNumberStep from '@app/containers/Modal/WelcomeSurveyModal/DepartmemtAndPhoneNumberStep';
import ExpectedUsageStep from '@app/containers/Modal/WelcomeSurveyModal/ExpectedUsageStep';
import {
  COMPANY_CATEGORIES,
  COMPANY_SCALES,
  DEPARTMENTS,
  PURPOSES,
} from '@app/containers/Modal/WelcomeSurveyModal/fixtures';
import CompanyNameStepMobile from '@app/containers/Modal/WelcomeSurveyModal/mobileSteps/CompanyNameStepMobile';
import CompanyScaleStepMobile from '@app/containers/Modal/WelcomeSurveyModal/mobileSteps/CompanyScaleStepMobile';
import JobRoleStepMobile from '@app/containers/Modal/WelcomeSurveyModal/mobileSteps/JobRoleMobile';
import PhoneNumberAndPurposeStep from '@app/containers/Modal/WelcomeSurveyModal/PhoneNumberAndPurposeStep';
import PhoneNumberStep from '@app/containers/Modal/WelcomeSurveyModal/PhoneNumberStep';
import PurposeStep from '@app/containers/Modal/WelcomeSurveyModal/PurposeStep';
import UserTypeStep from '@app/containers/Modal/WelcomeSurveyModal/UserTypeStep';
import useResponsiveValue from '@app/hooks/useResponsiveValue';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';
import pushDataLayer from '@app/libs/analytics/googleTagManager';
import { modalClose } from '@app/store/modal/actions';

export interface Survey {
  companyName: string;
  companyScale: (typeof COMPANY_SCALES)[number] | '';
  department: (typeof DEPARTMENTS)[number] | '';
  companyCategory: (typeof COMPANY_CATEGORIES)[number] | '';
  userType: string;
  expectedUsage: string;
  expectedStartTime: string;
  countryCode: string;
  phoneNumber: string;
  purpose: (typeof PURPOSES)[number] | '';
  purposeExtra: string;
  jobRole: string[];
}

function WelcomeSurveyModal({
  toPublicWorkspace = false,
  hasInvitation = false,
  onSubmit = () => {},
}: {
  toPublicWorkspace?: boolean;
  hasInvitation?: boolean;
  onSubmit: () => void;
}) {
  const dispatch = useDispatch();
  const workspaceId = useWorkspaceId();
  const [stepIndex, setStepIndex] = useState(0);
  const [survey, setSurvey] = useState<Survey>({
    companyName: '',
    companyScale: '',
    department: '',
    companyCategory: '',
    userType: '',
    expectedUsage: '',
    expectedStartTime: '',
    countryCode: 'KR' as CountryCode,
    purposeExtra: '',
    phoneNumber: '',
    purpose: '',
    jobRole: [],
  });

  const isMobile = useResponsiveValue([true, false]);

  const {
    data: workspaceSurveyData,
    refetch: refetchWorkspaceSurvey,
    isSuccess: isWorkspaceSurveyFetchSuccess,
  } = useGetWorkspaceSurvey(workspaceId, {
    query: { enabled: !!workspaceId },
  });
  const { data: user, isSuccess: isUserFetchSuccess, refetch: refetchUser } = useGetUserInfo();

  const { data: documentsAlreadyHaveCount } = useGetAllDocuments(
    workspaceId,
    {
      createdAt: JSON.stringify({ le: user?.createdAt }),
      limit: 1,
      offset: 0,
    },
    { query: { enabled: isUserFetchSuccess && !!workspaceId, select: (data) => data.count } }
  );

  const isSigner = !!documentsAlreadyHaveCount;

  const { mutate: mutateWorkspaceSurvey, isLoading: isWorkspaceSurveyLoading } =
    useSaveWorkspaceSurvey({
      mutation: {
        onSuccess: async (workspaceSurvey) => {
          pushDataLayer({ event: 'submitSignUpSurvey', workspaceSurvey });
          onSubmit();
          dispatch(modalClose());
          await Promise.all([refetchUser(), refetchWorkspaceSurvey()]);
        },
      },
    });

  const { mutate: mutateSurvey } = useSaveSurvey();

  const { title, subtitle } = useMemo(() => {
    // 공용 웤스로 전환하려는 유저
    if (toPublicWorkspace) {
      return {
        title: '공용 워크스페이스 설정 중입니다',
        subtitle: '워크스페이스 설정에 필요한 정보를 입력해 주세요.',
      };
    }

    // 초대 받은 유저
    if (hasInvitation) {
      return {
        title: '모두싸인 가입을 환영합니다!',
        subtitle: '초대받으신 워크스페이스에 대한 정보를 기입해 주세요.',
      };
    }

    // 서명 한 적이 있는 유저
    if (isSigner) {
      return {
        title: '모두싸인 첫 서명을 축하드려요!',
        subtitle: '서명 완료 문서 확인을 위한 모두싸인 설정이 필요합니다.',
      };
    }

    // 일반 유저
    return {
      title: '모두싸인 가입을 환영합니다!',
      subtitle: '우리 비즈니스에 맞는 문서함을 설정해 볼게요.',
    };
  }, [toPublicWorkspace, hasInvitation, isSigner]);

  const nextStep = () => setStepIndex((prev) => prev + 1);

  const submitSurvey = useCallback(() => {
    const isSupported = isSupportedCountry(survey.countryCode);
    const internationalPhoneNumber =
      (parsePhoneNumberFromString(
        survey.phoneNumber,
        (isSupported ? survey.countryCode : 'US') as CountryCode
      )?.number as string) || survey.phoneNumber;

    mutateSurvey({
      data: {
        userType: survey.userType || null,
        phoneNumber: internationalPhoneNumber || null,
        companyName: survey.companyName || null,
        companyScale: survey.companyScale || null,
        companyCategory: survey.companyCategory || null,
        department: survey.department || null,
        expectedUsage: survey.expectedUsage || null,
        expectedStartTime: survey.expectedStartTime || null,
        searchWord: null,
        city: null,
        funnel: null,
      },
    });
    mutateWorkspaceSurvey({
      workspaceId,
      data: {
        survey: {
          userType: survey.userType || null,
          companyName: survey.companyName || null,
          companyScale: survey.companyScale || null,
          companyCategory: survey.companyCategory || null,
          department: survey.department || null,
          expectedUsage: survey.expectedUsage || null,
          expectedStartTime: survey.expectedStartTime || null,
          purpose:
            survey.purpose === '기타'
              ? `${survey.purpose}/ ${survey.purposeExtra}`
              : survey.purpose || null,
          jobRole: survey.jobRole.sort().join(', ') || null, // 역할은 항상 사전 순으로 정렬해서 저장
          isSigner: isSigner.toString(),
        },
      },
    });
  }, [
    isSigner,
    mutateSurvey,
    mutateWorkspaceSurvey,
    survey.companyCategory,
    survey.companyName,
    survey.companyScale,
    survey.countryCode,
    survey.department,
    survey.expectedStartTime,
    survey.expectedUsage,
    survey.jobRole,
    survey.phoneNumber,
    survey.purpose,
    survey.purposeExtra,
    survey.userType,
    workspaceId,
  ]);

  const step = useMemo(() => {
    // 초대장이 활성화된 경우 최소한의 정보만 받는다.
    if (!toPublicWorkspace && hasInvitation) {
      return (
        <DepartmemtAndPhoneNumberStep
          isWorkspaceSurveyLoading={isWorkspaceSurveyLoading}
          setSurvey={setSurvey}
          submitSurvey={submitSurvey}
          survey={survey}
        />
      );
    }
    // 개인 이용자인 경우 설문 항목을 줄인다.
    if (!toPublicWorkspace && survey.userType === '개인 이용자') {
      switch (stepIndex) {
        case 0:
          return <UserTypeStep nextStep={nextStep} setSurvey={setSurvey} />;
        case 1:
          return <PurposeStep nextStep={nextStep} setSurvey={setSurvey} survey={survey} />;
        case 2:
          return <ExpectedUsageStep nextStep={nextStep} setSurvey={setSurvey} survey={survey} />;
        case 3:
          return (
            <PhoneNumberStep
              isWorkspaceSurveyLoading={isWorkspaceSurveyLoading}
              setSurvey={setSurvey}
              submitSurvey={submitSurvey}
              survey={survey}
            />
          );
        default:
          return null;
      }
    }

    // 단체 이용자인 경우 모바일과 PC의 설문 순서를 다르게 한다.
    if (isMobile) {
      switch (stepIndex) {
        case 0:
          return <UserTypeStep nextStep={nextStep} setSurvey={setSurvey} />;
        case 1:
          return (
            <CompanyNameStepMobile nextStep={nextStep} setSurvey={setSurvey} survey={survey} />
          );
        case 2:
          return <JobRoleStepMobile nextStep={nextStep} setSurvey={setSurvey} survey={survey} />;
        case 3:
          return (
            <CompanyScaleStepMobile nextStep={nextStep} setSurvey={setSurvey} survey={survey} />
          );
        case 4:
          return <ExpectedUsageStep nextStep={nextStep} setSurvey={setSurvey} survey={survey} />;
        case 5:
          return (
            <PhoneNumberAndPurposeStep
              isWorkspaceSurveyLoading={isWorkspaceSurveyLoading}
              setSurvey={setSurvey}
              submitSurvey={submitSurvey}
              survey={survey}
            />
          );
        default:
          return null;
      }
    }
    switch (stepIndex) {
      case 0:
        return <UserTypeStep nextStep={nextStep} setSurvey={setSurvey} />;
      case 1:
        return <CompanyNameStep nextStep={nextStep} setSurvey={setSurvey} survey={survey} />;
      case 2:
        return <CompanyScaleStep nextStep={nextStep} setSurvey={setSurvey} survey={survey} />;
      case 3:
        return <ExpectedUsageStep nextStep={nextStep} setSurvey={setSurvey} survey={survey} />;
      case 4:
        return (
          <PhoneNumberAndPurposeStep
            isWorkspaceSurveyLoading={isWorkspaceSurveyLoading}
            setSurvey={setSurvey}
            submitSurvey={submitSurvey}
            survey={survey}
          />
        );
      default:
        return null;
    }
  }, [
    hasInvitation,
    isMobile,
    isWorkspaceSurveyLoading,
    stepIndex,
    submitSurvey,
    survey,
    toPublicWorkspace,
  ]);

  const lastStepIndex = useMemo(() => {
    if (!toPublicWorkspace && hasInvitation) return 0;
    if (survey.userType === '개인 이용자' || !survey.userType) {
      return 3;
    }
    return isMobile ? 5 : 4;
  }, [hasInvitation, isMobile, survey.userType, toPublicWorkspace]);

  // 모바일 여부가 변경되거나 초대장이 활성화되면 설문을 초기화한다.
  useEffect(() => {
    // 초대장이 활성화되었거나 공용 웤스로 전환하는 유저의 경우 단체 이용자로 자동 설정하고 첫 단계 건너 뜀
    if (toPublicWorkspace || hasInvitation) {
      setSurvey((prev) => ({
        ...prev,
        userType: '회사·단체·개인 사업자',
      }));
      setStepIndex(1);
      return;
    }
    setStepIndex(0);
  }, [hasInvitation, isMobile, toPublicWorkspace]);

  // 모달 조회 이벤트
  useEffect(() => {
    const started = Date.now();
    return () =>
      pushDataLayer({
        event: 'leaveSignUpSurvey',
        viewFragment: stepIndex + 1,
        viewDuration: Date.now() - started,
      });
  }, [stepIndex]);

  useEffect(() => {
    if (isWorkspaceSurveyFetchSuccess && workspaceSurveyData.survey) {
      // 이미 제출한 설문 정보는 미리 채워둔다.
      setSurvey((prev) => ({
        ...prev,
        companyName: workspaceSurveyData.survey.companyName || '',
        companyScale: workspaceSurveyData.survey.companyScale || '',
        department: workspaceSurveyData.survey.department || '',
        companyCategory: workspaceSurveyData.survey.companyCategory || '',
        // 공용 웤스로 전환하는 경우 이전 설문 정보와 상관 없이 회사·단체·개인 사업자로 설정한다.
        userType: toPublicWorkspace
          ? '회사·단체·개인 사업자'
          : workspaceSurveyData.survey.userType || '',
        expectedUsage: workspaceSurveyData.survey.expectedUsage || '',
        expectedStartTime: workspaceSurveyData.survey.expectedStartTime || '',
        purpose: workspaceSurveyData.survey.purpose?.startsWith('기타/ ')
          ? '기타'
          : workspaceSurveyData.survey.purpose || '',
        purposeExtra: workspaceSurveyData.survey.purpose?.startsWith('기타/ ')
          ? workspaceSurveyData.survey.purpose.replace('기타/ ', '')
          : '',
        jobRole: workspaceSurveyData.survey.jobRole?.split(', ') || [],
      }));
    }
  }, [workspaceSurveyData?.survey, isWorkspaceSurveyFetchSuccess, toPublicWorkspace]);

  return (
    <Flex
      flexDirection="column"
      height="100vh"
      my={[null, '-40px', '-66px']}
      overflowY="auto"
      px={[null, 7]}
      py={[null, 6]}
    >
      <Flex
        alignItems="center"
        backgroundColor="white"
        borderRadius={[null, '6px']}
        flex={1}
        flexDirection="column"
        lineHeight={1.5}
      >
        <Box
          backgroundColor="darkNavy600"
          borderTopLeftRadius={[null, '6px']}
          borderTopRightRadius={[null, '6px']}
          color="white"
          pb={8}
          pt={[7, 9]}
          px={[7, '40px']}
          textAlign="center"
          width="100%"
        >
          <Box fontSize="18px" fontWeight="medium">
            {title}
          </Box>
          <Box mt={3}>{subtitle}</Box>
        </Box>
        <Flex flex={1} flexDirection="column" maxWidth="450px" py={[9, 10]} width="100%">
          <Box flex={1} px={[7, 0]}>
            {!!lastStepIndex && (
              <Flex alignItems="center">
                <Box color="textEmphasisBase" fontSize={0} fontWeight="bold" mr={2}>
                  {stepIndex === lastStepIndex ? '마지막' : `${stepIndex + 1}/${lastStepIndex + 1}`}{' '}
                  단계 진행 중
                </Box>
                {Array.from(Array(lastStepIndex + 1).keys()).map((i) => (
                  <Box
                    backgroundColor={i > stepIndex ? 'pureNavy100' : 'amberYellow800'}
                    borderRadius="10px"
                    height="8px"
                    key={i}
                    ml={2}
                    width="8px"
                  />
                ))}
              </Flex>
            )}
            {step}
          </Box>
          <Flex justifyContent="center" mt={11}>
            <ModusignLogoFull color="darkGray200" opacity={0.4} width={120} />
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
}

export default WelcomeSurveyModal;
